<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="w-full px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-8">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{{ t('usage.title') }}</h1>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">{{ t('usage.subtitle') }}</p>
          </div>
          <div class="mt-4 sm:mt-0">
            <div class="flex flex-wrap">
              <button
                class="mr-3 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                @click="openPackageRequestModal">
                {{ t('usage.purchaseMore') }}
              </button>
              <button 
                class="btn" 
                @click="reload" 
                :disabled="loading === true">
                <svg v-if="loading === false" class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <svg v-else class="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ loading === true ? t('usage.loading') : t('usage.refresh') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-8">
        <div v-if="showUsageSkeleton === true" class="flex flex-wrap -mx-3">
          <div v-for="item in 2" :key="`usage-summary-skeleton-${item}`" class="w-full lg:w-1/2 px-3 pb-6">
            <div class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 h-full min-h-[208px]">
              <div class="flex items-center justify-between mb-6">
                <div class="w-full max-w-xs">
                  <div class="h-3 w-24 rounded skeleton"></div>
                  <div class="mt-3 h-6 w-40 rounded skeleton"></div>
                </div>
                <div class="h-12 w-12 rounded-lg skeleton"></div>
              </div>
              <div class="mb-4">
                <div class="flex items-baseline justify-between mb-2">
                  <div class="h-4 w-16 rounded skeleton"></div>
                  <div class="h-8 w-20 rounded skeleton"></div>
                </div>
                <div class="h-2 rounded-full skeleton"></div>
              </div>
              <div class="grid grid-cols-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <div class="h-3 w-16 rounded skeleton"></div>
                  <div class="mt-2 h-5 w-20 rounded skeleton"></div>
                </div>
                <div>
                  <div class="h-3 w-12 rounded skeleton"></div>
                  <div class="mt-2 h-5 w-20 rounded skeleton"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-wrap -mx-3">
          <div class="w-full lg:w-1/2 px-3 pb-6">
            <div class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 h-full min-h-[208px]">
              <div class="flex items-center justify-between mb-6">
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ t('usage.messagesLabel') }}</p>
                  <h2 class="mt-1 text-xl font-bold text-gray-900 dark:text-white">{{ t('usage.messageCreditsTitle') }}</h2>
                </div>
                <div class="h-12 w-12 rounded-lg bg-amber-50 dark:bg-amber-900/40 flex items-center justify-center">
                  <svg class="h-6 w-6 text-amber-600 dark:text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div class="mb-4">
                <div class="flex items-baseline justify-between mb-2">
                  <span class="text-sm font-medium text-gray-600 dark:text-gray-400">{{ t('usage.used') }}</span>
                  <span class="text-2xl font-bold text-gray-900 dark:text-white">{{ messageSummary.used }}</span>
                </div>
                <div class="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div class="h-full bg-amber-600 dark:bg-amber-300 rounded-full" :style="progressStyle(messageSummary.percent)"></div>
                </div>
              </div>
              <div class="grid grid-cols-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ t('usage.remaining') }}</p>
                  <p class="mt-1 text-lg font-semibold text-gray-900 dark:text-white">{{ messageSummary.remaining }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ t('usage.total') }}</p>
                  <p class="mt-1 text-lg font-semibold text-gray-900 dark:text-white">{{ messageSummary.total }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full lg:w-1/2 px-3 pb-6">
            <div class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 h-full min-h-[208px]">
              <div class="flex items-center justify-between mb-6">
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ t('usage.voiceLabel') }}</p>
                  <h2 class="mt-1 text-xl font-bold text-gray-900 dark:text-white">{{ t('usage.voiceMinutesTitle') }}</h2>
                </div>
                <div class="h-12 w-12 rounded-lg bg-amber-50 dark:bg-amber-900/40 flex items-center justify-center">
                  <svg class="h-6 w-6 text-amber-600 dark:text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
              </div>
              <div class="mb-4">
                <div class="flex items-baseline justify-between mb-2">
                  <span class="text-sm font-medium text-gray-600 dark:text-gray-400">{{ t('usage.used') }}</span>
                  <span class="text-2xl font-bold text-gray-900 dark:text-white">{{ voiceSummary.used }}</span>
                </div>
                <div class="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div class="h-full bg-amber-600 dark:bg-amber-300 rounded-full" :style="progressStyle(voiceSummary.percent)"></div>
                </div>
              </div>
              <div class="grid grid-cols-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ t('usage.remaining') }}</p>
                  <p class="mt-1 text-lg font-semibold text-gray-900 dark:text-white">{{ voiceSummary.remaining }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ t('usage.total') }}</p>
                  <p class="mt-1 text-lg font-semibold text-gray-900 dark:text-white">{{ voiceSummary.total }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-8">
        <div v-if="showUsageSkeleton === true" class="flex flex-wrap -mx-3">
          <div v-for="item in 3" :key="`message-detail-skeleton-${item}`" class="w-full lg:w-1/3 px-3 pb-6">
            <div class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 h-full min-h-[180px]">
              <div class="mb-4">
                <div class="h-3 w-20 rounded skeleton mb-2"></div>
                <div class="h-5 w-32 rounded skeleton"></div>
              </div>
              <div class="h-9 w-24 rounded skeleton mb-3"></div>
              <div class="h-4 w-full rounded skeleton"></div>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-wrap -mx-3">
          <div v-for="card in messageDetails" :key="card.title" class="w-full lg:w-1/3 px-3 pb-6">
            <div class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 h-full min-h-[180px]">
              <div class="flex items-start justify-between mb-4">
                <div class="flex-1">
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{{ card.subtitle }}</p>
                  <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ card.title }}</h3>
                </div>
              </div>
              <p class="text-3xl font-bold text-gray-900 dark:text-white mb-3">{{ card.value }}</p>
              <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{{ card.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-8">
        <div v-if="showUsageSkeleton === true" class="flex flex-wrap -mx-3">
          <div v-for="item in 3" :key="`voice-detail-skeleton-${item}`" class="w-full lg:w-1/3 px-3 pb-6">
            <div class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 h-full min-h-[180px]">
              <div class="mb-4">
                <div class="h-3 w-20 rounded skeleton mb-2"></div>
                <div class="h-5 w-32 rounded skeleton"></div>
              </div>
              <div class="h-9 w-24 rounded skeleton mb-3"></div>
              <div class="h-4 w-full rounded skeleton"></div>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-wrap -mx-3">
          <div v-for="card in voiceDetails" :key="card.title" class="w-full lg:w-1/3 px-3 pb-6">
            <div class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 h-full min-h-[180px]">
              <div class="flex items-start justify-between mb-4">
                <div class="flex-1">
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{{ card.subtitle }}</p>
                  <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ card.title }}</h3>
                </div>
              </div>
              <p class="text-3xl font-bold text-gray-900 dark:text-white mb-3">{{ card.value }}</p>
              <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{{ card.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-8">
        <div v-if="showAlertsSkeleton === true" class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 min-h-[284px]">
          <div class="flex flex-col md:flex-row md:items-start md:justify-between">
            <div class="w-full max-w-xl mb-4 md:mb-0">
              <div class="h-3 w-24 rounded skeleton"></div>
              <div class="mt-3 h-6 w-64 rounded skeleton"></div>
              <div class="mt-3 h-4 w-full rounded skeleton"></div>
            </div>
            <div class="h-10 w-28 rounded-lg skeleton"></div>
          </div>
          <div class="mt-6 flex flex-wrap -mx-3">
            <div class="w-full lg:w-2/3 px-3 pb-6">
              <div class="h-4 w-24 rounded skeleton mb-2"></div>
              <div class="h-32 w-full rounded-lg skeleton"></div>
            </div>
            <div class="w-full lg:w-1/3 px-3 pb-6">
              <div class="h-4 w-24 rounded skeleton mb-2"></div>
              <div class="h-12 w-full rounded-lg skeleton mb-3"></div>
              <div class="h-12 w-full rounded-lg skeleton"></div>
            </div>
          </div>
        </div>
        <div v-else class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 min-h-[284px]">
          <div class="flex flex-col md:flex-row md:items-start md:justify-between">
            <div class="mb-4 md:mb-0">
              <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Usage alerts</p>
              <h2 class="mt-1 text-xl font-bold text-gray-900 dark:text-white">Email notifications for 80% and 100%</h2>
              <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">Add one or more email addresses to receive alerts when message or voice usage reaches the configured thresholds.</p>
            </div>
            <button
              class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
              :disabled="alertsSaving === true"
              @click="saveUsageAlertSettings">
              {{ alertsSaving === true ? 'Saving...' : 'Save alerts' }}
            </button>
          </div>
          <div class="mt-6 flex flex-wrap -mx-3">
            <div class="w-full lg:w-2/3 px-3 pb-6">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recipients</label>
              <textarea
                v-model="usageAlertSettings.emails"
                rows="5"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-3"
                placeholder="alerts@example.com"></textarea>
              <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">Use commas or new lines between email addresses.</p>
            </div>
            <div class="w-full lg:w-1/3 px-3 pb-6">
              <p class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Thresholds</p>
              <label class="flex items-center rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3 mb-3">
                <input v-model="usageAlertSettings.alertAt80" type="checkbox" class="checkbox checkbox-sm" />
                <span class="ml-3 text-sm text-gray-700 dark:text-gray-300">Send at 80% usage</span>
              </label>
              <label class="flex items-center rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
                <input v-model="usageAlertSettings.alertAt100" type="checkbox" class="checkbox checkbox-sm" />
                <span class="ml-3 text-sm text-gray-700 dark:text-gray-300">Send at 100% usage</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800">
        <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-800">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ t('usage.historical.heading') }}</p>
              <h2 class="mt-1 text-xl font-bold text-gray-900 dark:text-white">{{ t('usage.historical.subtitle') }}</h2>
      </div>
    </div>
        </div>
        <div class="px-6 py-6">
          <div v-if="showUsageSkeleton === true">
            <div class="h-4 w-full rounded skeleton mb-4"></div>
            <div class="h-4 w-full rounded skeleton mb-4"></div>
            <div class="h-4 w-full rounded skeleton mb-4"></div>
            <div class="h-4 w-full rounded skeleton"></div>
          </div>
          <div v-else-if="monthlyRows.length > 0" class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
            <thead>
              <tr>
                  <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ t('usage.historical.period') }}</th>
                  <th class="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ t('usage.historical.messagesUsed') }}</th>
                  <th class="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ t('usage.historical.voiceUsed') }}</th>
              </tr>
            </thead>
              <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                <tr v-for="row in monthlyRows" :key="row.period" class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150">
                  <td class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{{ formatPeriodLabel(row.period) }}</td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-300 font-medium">{{ formatNumber(row.messages) }}</td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-300 font-medium">{{ formatNumber(row.voice) }}</td>
              </tr>
            </tbody>
          </table>
          </div>
          <div v-else class="py-16 text-center">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">{{ t('usage.historical.emptyTitle') }}</p>
            <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">{{ t('usage.historical.emptySubtitle') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="packageRequestModalOpen === true" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" @click="closePackageRequestModal">
      <div class="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900" @click.stop>
        <div class="flex items-start justify-between">
          <div>
            <h2 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('usage.packageRequest.title') }}</h2>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">{{ t('usage.packageRequest.subtitle') }}</p>
          </div>
          <button type="button" class="ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" @click="closePackageRequestModal">x</button>
        </div>
        <div class="mt-6">
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ t('usage.packageRequest.requestType') }}</label>
          <select v-model="packageRequestForm.requestType" class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
            <option value="purchase">{{ t('usage.packageRequest.purchaseOption') }}</option>
            <option value="monthly_increase">{{ t('usage.packageRequest.monthlyIncreaseOption') }}</option>
          </select>
        </div>
        <div class="mt-4">
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ t('usage.packageRequest.packageId') }}</label>
          <CustomDropdownSelector
            v-if="packageRequestPackageOptions.length > 0 || packageRequestPackagesLoading === true"
            v-model="packageRequestForm.packageId"
            class="w-full"
            :disabled="packageRequestPackagesLoading === true"
            :options="packageRequestPackageOptions"
            :placeholder="packageRequestPackagesLoading === true ? t('usage.packageRequest.loadingPackages', 'Loading packages...') : t('usage.packageRequest.selectPackagePlaceholder', 'Select a package')"
            buttonClass="!h-[48px] !min-h-[48px] !w-full !rounded-lg !border !border-gray-300 !bg-white !px-4 !py-3 !text-gray-900 dark:!border-gray-700 dark:!bg-gray-800 dark:!text-white"
          />
          <input v-else v-model="packageRequestForm.packageId" type="text" class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white" :placeholder="t('usage.packageRequest.packageIdPlaceholder')" />
        </div>
        <div class="mt-4">
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ t('usage.packageRequest.message') }}</label>
          <textarea v-model="packageRequestForm.messageText" rows="4" class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white" :placeholder="t('usage.packageRequest.messagePlaceholder')"></textarea>
        </div>
        <div class="mt-6 flex justify-end">
          <button type="button" class="btn mr-3" @click="closePackageRequestModal">
            {{ t('usage.packageRequest.cancel') }}
          </button>
          <button type="button" class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500" :disabled="packageRequestSubmitting === true" @click="submitPackageRequest">
            {{ packageRequestSubmitting === true ? t('usage.packageRequest.submitting') : t('usage.packageRequest.submit') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from '@/i18n';
import axiosInstance from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState';
import CustomDropdownSelector from '@/components/CustomDropdownSelector.vue';

const { t } = useI18n();
const toast = useToast();
const loading = ref(false);
const usage = ref(createEmptyUsage());
const hasLoadedUsage = ref(false);
const alertsLoading = ref(false);
const hasLoadedAlerts = ref(false);
const alertsSaving = ref(false);
const usageAlertSettings = ref(createEmptyUsageAlertSettings());
const packageRequestModalOpen = ref(false);
const packageRequestSubmitting = ref(false);
const packageRequestForm = ref(createEmptyPackageRequestForm());
const packageRequestPackages = ref([]);
const packageRequestPackagesLoading = ref(false);
const hasLoadedPackageRequestPackages = ref(false);

const showUsageSkeleton = computed(() => loading.value === true || hasLoadedUsage.value === false);
const showAlertsSkeleton = computed(() => alertsLoading.value === true || hasLoadedAlerts.value === false);
const packageRequestPackageOptions = computed(() => packageRequestPackages.value.map(item => ({
  value: item.packageId,
  label: formatPackageRequestPackageLabel(item)
})));

function createEmptyUsageAlertSettings() {
  return {
    emails: '',
    alertAt80: true,
    alertAt100: true
  };
}

function createEmptyUsage() {
  return {
    messages: { totalCredits: null, totalUsed: 0, remaining: null, monthly: [] },
    voiceMinutes: { totalCredits: null, totalUsed: 0, remaining: null, monthly: [] }
  };
}

function createEmptyPackageRequestForm() {
  return {
    requestType: 'purchase',
    packageId: '',
    messageText: ''
  };
}

function normalizePackageRequestPackagesPayload(payload) {
  const items = Array.isArray(payload?.packages) === true ? payload.packages : (Array.isArray(payload) === true ? payload : []);
  const normalized = [];
  for (const item of items) {
    if (item === undefined || item === null || typeof item !== 'object') {
      continue;
    }
    const packageId = typeof item.packageId === 'string' ? item.packageId.trim() : '';
    if (packageId === '') {
      continue;
    }
    normalized.push({
      packageId,
      packageName: typeof item.packageName === 'string' ? item.packageName.trim() : '',
      packageType: typeof item.packageType === 'string' ? item.packageType.trim() : '',
      billingCycle: typeof item.billingCycle === 'string' ? item.billingCycle.trim() : '',
      price: Number.isFinite(Number(item.price)) === true ? Number(item.price) : null,
      currency: typeof item.currency === 'string' ? item.currency.trim() : ''
    });
  }
  return normalized;
}

function formatPackageRequestPackageLabel(item) {
  const packageName = typeof item?.packageName === 'string' ? item.packageName.trim() : '';
  const packageId = typeof item?.packageId === 'string' ? item.packageId.trim() : '';
  if (packageName === '') {
    return packageId;
  }
  if (packageName === packageId) {
    return packageName;
  }
  return `${packageName} (${packageId})`;
}

function normalizeUsagePayload(payload) {
  const normalized = createEmptyUsage();
  if (payload !== undefined && payload !== null) {
    if (payload.messages !== undefined && payload.messages !== null) {
      const section = payload.messages;
      if (section.totalCredits === null) {
        normalized.messages.totalCredits = null;
      } else if (Number.isFinite(Number(section.totalCredits)) === true) {
        normalized.messages.totalCredits = Number(section.totalCredits);
      }
      if (Number.isFinite(Number(section.totalUsed)) === true) {
        normalized.messages.totalUsed = Number(section.totalUsed);
      }
      if (section.remaining === null) {
        normalized.messages.remaining = null;
      } else if (Number.isFinite(Number(section.remaining)) === true) {
        normalized.messages.remaining = Number(section.remaining);
      }
      normalized.messages.monthly = Array.isArray(section.monthly) === true ? section.monthly.map(item => ({
        periodStart: item.periodStart,
        usedMessages: Number.isFinite(Number(item.usedMessages)) === true ? Number(item.usedMessages) : 0
      })) : [];
    }
    if (payload.voiceMinutes !== undefined && payload.voiceMinutes !== null) {
      const section = payload.voiceMinutes;
      if (section.totalCredits === null) {
        normalized.voiceMinutes.totalCredits = null;
      } else if (Number.isFinite(Number(section.totalCredits)) === true) {
        normalized.voiceMinutes.totalCredits = Number(section.totalCredits);
      }
      if (Number.isFinite(Number(section.totalUsed)) === true) {
        normalized.voiceMinutes.totalUsed = Number(section.totalUsed);
      }
      if (section.remaining === null) {
        normalized.voiceMinutes.remaining = null;
      } else if (Number.isFinite(Number(section.remaining)) === true) {
        normalized.voiceMinutes.remaining = Number(section.remaining);
      }
      normalized.voiceMinutes.monthly = Array.isArray(section.monthly) === true ? section.monthly.map(item => ({
        periodStart: item.periodStart,
        usedVoiceMinutes: Number.isFinite(Number(item.usedVoiceMinutes)) === true ? Number(item.usedVoiceMinutes) : 0
      })) : [];
    }
  }
  return normalized;
}

function normalizeUsageAlertSettingsPayload(payload) {
  const normalized = createEmptyUsageAlertSettings();
  const emailItems = Array.isArray(payload?.emails) === true ? payload.emails : [];
  const emailList = [];
  for (const item of emailItems) {
    if (typeof item !== 'string') {
      continue;
    }
    const trimmed = item.trim();
    if (trimmed === '') {
      continue;
    }
    emailList.push(trimmed);
  }
  normalized.emails = emailList.join('\n');
  normalized.alertAt80 = payload?.alertAt80 !== false;
  normalized.alertAt100 = payload?.alertAt100 !== false;
  return normalized;
}

function buildMetric(rawValue) {
  if (rawValue === undefined || rawValue === null) {
    return { value: 0, displayText: 'Unlimited' };
  }
  if (Number.isFinite(Number(rawValue)) === false) {
    return { value: 0, displayText: 'Unlimited' };
  }
  return { value: Number(rawValue), displayText: '' };
}

function formatMetricDisplay(rawValue) {
  const metric = buildMetric(rawValue);
  if (metric.displayText !== '') {
    return metric.displayText;
  }
  return formatNumber(metric.value);
}

function computeUsagePercent(usedValue, totalValue) {
  if (totalValue === undefined || totalValue === null) {
    return null;
  }
  const totalNumber = Number(totalValue);
  if (Number.isFinite(totalNumber) === false) {
    return null;
  }
  if (totalNumber < 0) {
    return null;
  }
  if (totalNumber === 0) {
    return 100;
  }
  const usedNumber = Number(usedValue);
  if (Number.isFinite(usedNumber) === false) {
    return 0;
  }
  if (usedNumber <= 0) {
    return 0;
  }
  if (usedNumber >= totalNumber) {
    return 100;
  }
  return Math.round((usedNumber / totalNumber) * 100);
}

function buildProgressWidth(percent) {
  if (percent === null) {
    return '100%';
  }
  if (percent <= 0) {
    return '6%';
  }
  if (percent >= 100) {
    return '100%';
  }
  return `${percent}%`;
}

function progressStyle(percent) {
  return { width: buildProgressWidth(percent) };
}

const messageSummary = computed(() => {
  const total = formatMetricDisplay(usage.value.messages.totalCredits);
  const used = formatMetricDisplay(usage.value.messages.totalUsed);
  const remaining = formatMetricDisplay(usage.value.messages.remaining);
  const percent = computeUsagePercent(usage.value.messages.totalUsed, usage.value.messages.totalCredits);
  return { total, used, remaining, percent };
});

const voiceSummary = computed(() => {
  const total = formatMetricDisplay(usage.value.voiceMinutes.totalCredits);
  const used = formatMetricDisplay(usage.value.voiceMinutes.totalUsed);
  const remaining = formatMetricDisplay(usage.value.voiceMinutes.remaining);
  const percent = computeUsagePercent(usage.value.voiceMinutes.totalUsed, usage.value.voiceMinutes.totalCredits);
  return { total, used, remaining, percent };
});

const messageDetails = computed(() => {
  const summary = messageSummary.value;
  return [
    { title: t('usage.details.messages.totalTitle'), subtitle: t('usage.details.allocation'), value: summary.total, description: t('usage.details.messages.totalDescription') },
    { title: t('usage.details.messages.usedTitle'), subtitle: t('usage.details.consumption'), value: summary.used, description: t('usage.details.messages.usedDescription') },
    { title: t('usage.details.messages.remainingTitle'), subtitle: t('usage.details.balance'), value: summary.remaining, description: t('usage.details.messages.remainingDescription') }
  ];
});

const voiceDetails = computed(() => {
  const summary = voiceSummary.value;
  return [
    { title: t('usage.details.voice.totalTitle'), subtitle: t('usage.details.allocation'), value: summary.total, description: t('usage.details.voice.totalDescription') },
    { title: t('usage.details.voice.usedTitle'), subtitle: t('usage.details.consumption'), value: summary.used, description: t('usage.details.voice.usedDescription') },
    { title: t('usage.details.voice.remainingTitle'), subtitle: t('usage.details.balance'), value: summary.remaining, description: t('usage.details.voice.remainingDescription') }
  ];
});

const monthlyRows = computed(() => {
  const combined = new Map();
  const messageMonthly = Array.isArray(usage.value.messages.monthly) === true ? usage.value.messages.monthly : [];
  for (const item of messageMonthly) {
    const period = item.periodStart;
    const messageValue = Number.isFinite(Number(item.usedMessages)) === true ? Number(item.usedMessages) : 0;
    combined.set(period, { period, messages: messageValue, voice: 0 });
  }
  const voiceMonthly = Array.isArray(usage.value.voiceMinutes.monthly) === true ? usage.value.voiceMinutes.monthly : [];
  for (const item of voiceMonthly) {
    const period = item.periodStart;
    const voiceValue = Number.isFinite(Number(item.usedVoiceMinutes)) === true ? Number(item.usedVoiceMinutes) : 0;
    if (combined.has(period) === true) {
      const existing = combined.get(period);
      existing.voice = voiceValue;
      combined.set(period, existing);
    } else {
      combined.set(period, { period, messages: 0, voice: voiceValue });
    }
  }
  const rows = Array.from(combined.values());
  rows.sort((a, b) => {
    const aTime = new Date(a.period).getTime();
    const bTime = new Date(b.period).getTime();
    return bTime - aTime;
  });
  return rows;
});

function formatPeriodLabel(value) {
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime()) === true) {
      return String(value);
    }
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    return `${year}-${month < 10 ? '0' + month : month}`;
  } catch (e) {
    return String(value);
  }
}

function formatNumber(value) {
  if (Number.isFinite(Number(value)) === true) {
    const numericValue = Number(value);
    if (Math.abs(numericValue - Math.round(numericValue)) < 0.000001) {
      return Math.round(numericValue).toLocaleString();
    }
    return numericValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  return '0';
}

async function loadUsage() {
  if (loading.value === true) {
    return;
  }
  loading.value = true;
  try {
    const base = globalState.apiEndpoint !== undefined && globalState.apiEndpoint !== null ? globalState.apiEndpoint : '';
    const url = base !== '' ? `${base}/api/reports/organizationUsage` : 'reports/organizationUsage';
    const response = await axiosInstance.get(url);
    usage.value = normalizeUsagePayload(response.data);
  } catch (error) {
    toast.error(t('usage.toast.loadError'));
  } finally {
    hasLoadedUsage.value = true;
    loading.value = false;
  }
}

async function loadUsageAlertSettings() {
  if (alertsLoading.value === true) {
    return;
  }
  alertsLoading.value = true;
  try {
    const base = globalState.apiEndpoint !== undefined && globalState.apiEndpoint !== null ? globalState.apiEndpoint : '';
    const url = base !== '' ? `${base}/api/reports/organizationUsageAlerts` : 'reports/organizationUsageAlerts';
    const response = await axiosInstance.get(url);
    usageAlertSettings.value = normalizeUsageAlertSettingsPayload(response.data);
  } catch (error) {
    toast.error('Failed to load usage alerts');
  } finally {
    hasLoadedAlerts.value = true;
    alertsLoading.value = false;
  }
}

async function saveUsageAlertSettings() {
  if (alertsSaving.value === true) {
    return;
  }
  alertsSaving.value = true;
  try {
    const base = globalState.apiEndpoint !== undefined && globalState.apiEndpoint !== null ? globalState.apiEndpoint : '';
    const url = base !== '' ? `${base}/api/reports/organizationUsageAlerts` : 'reports/organizationUsageAlerts';
    const response = await axiosInstance.put(url, {
      emails: usageAlertSettings.value.emails,
      alertAt80: usageAlertSettings.value.alertAt80,
      alertAt100: usageAlertSettings.value.alertAt100
    });
    usageAlertSettings.value = normalizeUsageAlertSettingsPayload(response.data);
    toast.success('Usage alerts saved');
  } catch (error) {
    toast.error('Failed to save usage alerts');
  } finally {
    alertsSaving.value = false;
  }
}

async function loadPackageRequestPackages(forceReload = false) {
  if (packageRequestPackagesLoading.value === true) {
    return;
  }
  if (forceReload !== true && hasLoadedPackageRequestPackages.value === true) {
    return;
  }
  packageRequestPackagesLoading.value = true;
  try {
    const base = globalState.apiEndpoint !== undefined && globalState.apiEndpoint !== null ? globalState.apiEndpoint : '';
    const url = base !== '' ? `${base}/api/reports/organizationPackageRequestPackages` : 'reports/organizationPackageRequestPackages';
    const response = await axiosInstance.get(url);
    packageRequestPackages.value = normalizePackageRequestPackagesPayload(response.data);
  } catch (error) {
    packageRequestPackages.value = [];
  } finally {
    hasLoadedPackageRequestPackages.value = true;
    packageRequestPackagesLoading.value = false;
  }
}

function openPackageRequestModal() {
  packageRequestForm.value = createEmptyPackageRequestForm();
  packageRequestModalOpen.value = true;
  loadPackageRequestPackages(true);
}

function closePackageRequestModal() {
  if (packageRequestSubmitting.value == true) {
    return;
  }
  packageRequestModalOpen.value = false;
}

async function submitPackageRequest() {
  if (packageRequestSubmitting.value == true) {
    return;
  }
  const packageId = typeof packageRequestForm.value.packageId === 'string' ? packageRequestForm.value.packageId.trim() : ''
  if (packageId === '') {
    toast.error(t('usage.packageRequest.error'))
    return
  }
  packageRequestSubmitting.value = true;
  try {
    const base = globalState.apiEndpoint !== undefined && globalState.apiEndpoint !== null ? globalState.apiEndpoint : '';
    const url = base !== '' ? `${base}/api/reports/organizationPackageRequest` : 'reports/organizationPackageRequest';
    await axiosInstance.post(url, {
      requestType: packageRequestForm.value.requestType,
      packageId,
      messageText: packageRequestForm.value.messageText
    });
    packageRequestModalOpen.value = false;
    toast.success(t('usage.packageRequest.success'));
  } catch (error) {
    toast.error(t('usage.packageRequest.error'));
  } finally {
    packageRequestSubmitting.value = false;
  }
}

function reload() {
  loadUsage();
  loadUsageAlertSettings();
}

onMounted(() => {
  loadUsage();
  loadUsageAlertSettings();
});
</script>
